'use client';

import * as React from 'react';

import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/primitives/icon';
import { cn } from '@/components/atoms/primitives/utils';
import type { NotificationItem } from '@loci/api';
import { collectUnreadIds, groupNotifications } from '@/lib/notifications/groupNotifications';

import { NotificationRow } from './NotificationRow';

/** Heading id wired to the region's `aria-labelledby`. */
const PANEL_TITLE_ID = 'notification-panel-title';

export interface NotificationPanelProps {
  /** Notification rows, newest-first (from `getNotifications`). */
  readonly notifications: readonly NotificationItem[];
  /** True while the initial list request is in flight → skeleton state. */
  readonly isLoading?: boolean;
  /** True when the list request failed → error/retry state. */
  readonly isError?: boolean;
  /** IANA timezone used for Today/Earlier grouping + relative-time copy. */
  readonly cityTz: string;
  /** Reference instant for grouping + relative time (injectable for tests). */
  readonly now?: Date;
  /**
   * Called once on mount with the ids of the currently-visible unread rows so
   * the host can optimistically `markRead({ all: false, ids })`. Not called
   * while loading / on error, or when there are no unread rows.
   */
  readonly onMarkReadOnOpen?: (unreadIds: string[]) => void;
  /**
   * Activate a row: deep-link to its `href` (when present) and mark that row
   * read. Owned by the host so the panel stays presentational.
   */
  readonly onActivateRow: (id: string, href: string | null) => void;
  /** Retry handler for the error state. */
  readonly onRetry?: () => void;
  /** Requests the panel close (Escape / dismiss). Wired to the host popover. */
  readonly onClose?: () => void;
  /**
   * When `true` (default), the panel captures `document.activeElement` on mount,
   * moves focus into itself, and restores focus to the previously-focused element
   * on unmount — correct for the standalone / Storybook case where nothing else
   * owns focus.
   *
   * Set to `false` when hosting inside a focus-managing container such as a Radix
   * Popover or Sheet (WEB-436), whose own focus capture/restore would otherwise
   * fight this effect (double-restore / flaky `previouslyFocused`). With
   * `manageFocus={false}` the panel still focuses its first row on mount and
   * keeps the internal roving-tabindex arrow navigation + Escape handling; it
   * simply does not capture or restore the outer `document.activeElement`.
   */
  readonly manageFocus?: boolean;
}

/** Loading skeleton — a handful of shimmer rows. */
function LoadingState(): React.ReactElement {
  return (
    <div data-testid="notification-panel-loading" className="space-y-1 p-2" aria-busy="true">
      {Array.from({ length: 4 }).map((_, idx) => (
        <div key={idx} className="flex items-start gap-3 px-3 py-3">
          <div className="bg-muted size-8 shrink-0 animate-pulse rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="bg-muted h-3.5 w-3/4 animate-pulse rounded" />
            <div className="bg-muted h-3 w-1/2 animate-pulse rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Empty "all caught up" state. */
function EmptyState(): React.ReactElement {
  return (
    <div
      data-testid="notification-panel-empty"
      className="flex flex-col items-center justify-center gap-2 px-6 py-12 text-center"
    >
      <span className="bg-muted text-muted-foreground flex size-12 items-center justify-center rounded-full">
        <Icon name="BellOff" className="size-5" aria-hidden="true" />
      </span>
      <p className="text-foreground text-sm font-medium">You&rsquo;re all caught up</p>
      <p className="text-muted-foreground text-sm">New notifications will show up here.</p>
    </div>
  );
}

/** Error state with a retry affordance. */
function ErrorState({ onRetry }: { onRetry?: () => void }): React.ReactElement {
  return (
    <div
      data-testid="notification-panel-error"
      role="alert"
      className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center"
    >
      <span className="bg-destructive/10 text-destructive flex size-12 items-center justify-center rounded-full">
        <Icon name="TriangleAlert" className="size-5" aria-hidden="true" />
      </span>
      <p className="text-foreground text-sm font-medium">Couldn&rsquo;t load notifications</p>
      {onRetry && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRetry}
          data-testid="notification-panel-retry"
        >
          Try again
        </Button>
      )}
    </div>
  );
}

/**
 * Presentational Notification Center surface (WEB-434). Renders the
 * `getNotifications` list grouped Today / Earlier, with loading / empty /
 * error states. The bell wiring (popover/sheet) lands in WEB-436 — this panel
 * is the focus-trapped *content* the bell opens, developed and reviewed in
 * isolation (Storybook + RTL).
 *
 * A11y:
 *   - The region carries `role="dialog"`, `aria-modal`, and `aria-labelledby`.
 *   - Rows form a roving-tabindex `listbox`: Tab lands on the active row,
 *     ArrowUp/ArrowDown move focus between rows, Home/End jump to ends, and
 *     Enter/Space activate the focused row's deep link.
 *   - Escape calls `onClose`.
 *   - Focus is moved into the panel on mount and restored to the previously
 *     focused element on unmount (the host popover also restores focus to the
 *     trigger; this guards the standalone case). Pass `manageFocus={false}` to
 *     defer that capture/restore to a focus-managing host (Radix Popover/Sheet,
 *     WEB-436) — roving-tabindex + Escape stay active either way.
 *
 * Mark-read-on-open: fires `onMarkReadOnOpen` exactly once on mount with the
 * visible unread ids. The host performs the optimistic cache update + toast /
 * rollback on failure (kept out of this presentational layer).
 */
export function NotificationPanel({
  notifications,
  isLoading = false,
  isError = false,
  cityTz,
  now,
  onMarkReadOnOpen,
  onActivateRow,
  onRetry,
  onClose,
  manageFocus = true,
}: NotificationPanelProps): React.ReactElement {
  const regionRef = React.useRef<HTMLDivElement | null>(null);
  const rowRefs = React.useRef<Map<number, HTMLButtonElement | null>>(new Map());
  const [focusedIndex, setFocusedIndex] = React.useState(0);

  const groups = React.useMemo(
    () => groupNotifications(notifications, cityTz, now),
    [notifications, cityTz, now],
  );

  // Flattened, ordered row list — the index space arrow keys navigate.
  const flatRows = React.useMemo(() => groups.flatMap((group) => group.items), [groups]);

  const hasRows = flatRows.length > 0;
  const showEmpty = !isLoading && !isError && !hasRows;
  const showList = !isLoading && !isError && hasRows;

  // Mark-read-on-open: fire once after mount when the list is ready + has
  // unread rows. The `firedRef` guard makes it idempotent across re-renders.
  const firedRef = React.useRef(false);
  React.useEffect(() => {
    if (firedRef.current) return;
    if (isLoading || isError) return;
    firedRef.current = true;
    const unreadIds = collectUnreadIds(flatRows);
    if (unreadIds.length > 0) {
      onMarkReadOnOpen?.(unreadIds);
    }
  }, [isLoading, isError, flatRows, onMarkReadOnOpen]);

  // Focus management: move focus into the panel on mount, restore on unmount.
  // Skipped entirely when `manageFocus={false}` so a focus-managing host (Radix
  // Popover/Sheet, WEB-436) owns capture/restore without this effect fighting it
  // (double-restore / flaky `previouslyFocused`). Roving-tabindex arrow nav +
  // Escape handling below remain active regardless.
  React.useEffect(() => {
    if (!manageFocus) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Defer so the first row ref is registered before we focus it.
    const raf = requestAnimationFrame(() => {
      const firstRow = rowRefs.current.get(0);
      if (firstRow) {
        firstRow.focus();
      } else {
        regionRef.current?.focus();
      }
    });
    return () => {
      cancelAnimationFrame(raf);
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus();
      }
    };
    // Mount-only: intentionally empty deps — focus is captured once on open
    // and restored once on close. `manageFocus` is read at mount and is not
    // expected to toggle for a given mounted instance.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const registerRef = React.useCallback((rowIndex: number, node: HTMLButtonElement | null) => {
    if (node) {
      rowRefs.current.set(rowIndex, node);
    } else {
      rowRefs.current.delete(rowIndex);
    }
  }, []);

  const moveFocus = React.useCallback(
    (nextIndex: number) => {
      const clamped = Math.max(0, Math.min(nextIndex, flatRows.length - 1));
      setFocusedIndex(clamped);
      rowRefs.current.get(clamped)?.focus();
    },
    [flatRows.length],
  );

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose?.();
        return;
      }
      if (!hasRows) return;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          moveFocus(focusedIndex + 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          moveFocus(focusedIndex - 1);
          break;
        case 'Home':
          event.preventDefault();
          moveFocus(0);
          break;
        case 'End':
          event.preventDefault();
          moveFocus(flatRows.length - 1);
          break;
        default:
          break;
      }
    },
    [focusedIndex, flatRows.length, hasRows, moveFocus, onClose],
  );

  // Track a running flat index so each group's rows get a unique navigation
  // index even though they render under separate headings.
  let runningIndex = -1;

  return (
    <div
      ref={regionRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={PANEL_TITLE_ID}
      tabIndex={-1}
      data-testid="notification-panel"
      onKeyDown={onKeyDown}
      className={cn(
        'bg-popover text-popover-foreground flex max-h-[32rem] w-[22rem] flex-col',
        'rounded-lg border shadow-md outline-none',
      )}
    >
      <header className="flex items-center justify-between border-b px-4 py-3">
        <h2 id={PANEL_TITLE_ID} className="text-foreground text-sm font-semibold">
          Notifications
        </h2>
        {onClose && (
          <Button
            type="button"
            variant="text"
            icon
            size="sm"
            onClick={onClose}
            aria-label="Close notifications"
            data-testid="notification-panel-close"
          >
            <Icon name="X" className="size-4" aria-hidden="true" />
          </Button>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading && <LoadingState />}
        {isError && <ErrorState onRetry={onRetry} />}
        {showEmpty && <EmptyState />}
        {showList && (
          <div className="py-1">
            {groups.map((group) => (
              <section
                key={group.id}
                aria-labelledby={`notification-group-${group.id}`}
                className="px-1"
              >
                <h3
                  id={`notification-group-${group.id}`}
                  className="text-muted-foreground px-3 pt-3 pb-1 text-xs font-semibold tracking-wide uppercase"
                >
                  {group.label}
                </h3>
                <ul className="m-0 list-none p-0">
                  {group.items.map((item) => {
                    runningIndex += 1;
                    const rowIndex = runningIndex;
                    return (
                      <li key={item.id}>
                        <NotificationRow
                          notification={item}
                          cityTz={cityTz}
                          now={now}
                          onActivate={onActivateRow}
                          isFocused={rowIndex === focusedIndex}
                          rowIndex={rowIndex}
                          registerRef={registerRef}
                        />
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
